import { Component, OnInit, Renderer2 } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SliderModule } from '@syncfusion/ej2-angular-inputs';
import { ActivatedRoute, Router } from '@angular/router';
import { FastCheckinService } from './fast-checkin.service';
import { FastCheckinType, ICheckinUserForeignParkingFastCheckinNoPaymentDTO, IStartForeignParkingPaymentProcessFastCheckinDTO, ParkingProcessZoneInfoDTO } from 'src/app/base/models/models.swagger.generated';
import { StepperAllModule, StepperModule } from '@syncfusion/ej2-angular-navigations';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { FastCheckInStepUserInfoComponent } from "./shared/fast-check-in-step-user-info/fast-checkin-step-user-info.component";
import { FastCeckinUserInfoFormType } from './shared/fast-check-in-step-user-info/fast-checkin-user-info-form';
import { FastCheckinStepDurationComponent } from './fast-checkin-step-duration/fast-checkin-step-duration.component';
import { QueryParameter } from 'src/app/base/query-parameters';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { FastCheckinDurationSubmitData } from './fast-checkin-step-duration/fast-checkin-step-duration.component';
import { MobileLandingService } from '../mobile-landing/mobile-landing.service';
import { MobileHeaderComponent } from '../mobile-landing/shared/mobile-header/mobile-header.component';

@Component({
  selector: 'app-fast-checkin',
  templateUrl: './fast-checkin.component.html',
  styleUrls: ['./fast-checkin.component.scss'],
  imports: [TranslateModule, ReactiveFormsModule, SliderModule, StepperAllModule, StepperModule, TooltipModule, FastCheckInStepUserInfoComponent, FastCheckinStepDurationComponent, MobileHeaderComponent],
})
export class FastCheckinComponent implements OnInit {
  public get zoneName(): string {
    return this.zoneInfo?.name ?? '';
  }

  public get controlPlate(): string {
    return this.userInfoForm?.controls.controlPlate?.value;
  }

  public fastCheckinType = FastCheckinType.FastCheckIn;
  public errorMessage: string = 'i18n.domain.fast-checkin.errors.zoneNotFound';
  public stepIndex = 0;
  public zoneInfo: ParkingProcessZoneInfoDTO | null = null;
  public userInfoForm: FormGroup<FastCeckinUserInfoFormType>;
  public showError = false;

  private _isProcessing = false;
  private selectedSubscriptionId = 0;

  constructor(
    private renderer: Renderer2,
    private route: ActivatedRoute,
    private fastCheckinService: FastCheckinService,
    private router: Router,
    private spinnerService: SpinnerService,
    private mobileLandingService: MobileLandingService
  ) { }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const zoneId = params[QueryParameter.ZoneId];
      this.mobileLandingService.fetchParkingProcessZoneInfo(zoneId).subscribe({next:zoneInfo => {
        this.zoneInfo = zoneInfo;
          if (!this.zoneInfo || (!this.zoneInfo.fastCheckinSubscriptionId)) {
            this.errorMessage = 'i18n.domain.fast-checkin.errors.zoneNotFound';
            this.showError = true;
          }
        },
        error: err => {
          this.errorMessage = 'i18n.domain.fast-checkin.errors.zoneNotFound';
          this.showError = true;
        }}
      );
    });

    this.renderer.addClass(document.body, 'body-login');
  }

  public onUserInfoNext(userInfoForm: FormGroup<FastCeckinUserInfoFormType>) {
    this.userInfoForm = userInfoForm;
    this.stepIndex = 1;
  }

  public onDurationSubmit(data: FastCheckinDurationSubmitData) {
    this.selectedSubscriptionId = data.selectedSubscriptionId;
    if (this.fastCheckinService.lastParkingPriceResult == null) {
      return;
    }
    if (this.fastCheckinService.lastParkingPriceResult.needsForeignParkingPaymentProcess === true) {
      this.startPaymentProcess(data.durationMinutes);
    } else {
      this.checkInWithoutPayment(data.durationMinutes);
    }
  }

  public onBack() {
    this.stepIndex = 0;
  }

  private startPaymentProcess(duration: number) {
    if (this._isProcessing) {
      return;
    }
    this._isProcessing = true;

    const data : IStartForeignParkingPaymentProcessFastCheckinDTO = {
      zoneId: this.zoneInfo.id,
      email: this.userInfoForm.controls.email.value,
      controlPlateNumber: this.userInfoForm.controls.controlPlate.value,
      controlPlateCountryCode: null,
      controlPlateType: null,
      maxIntendedParkingDurationMinutes: duration,
      selectedSubscriptionId: this.selectedSubscriptionId,
      parkingSpaceNumber: undefined, // TODO
      parkingPriceDetails: this.fastCheckinService.lastParkingPriceResult?.parkingPriceDetails,
      fallbackParkingPriceDetails: this.fastCheckinService.lastParkingPriceResult?.fallbackParkingPriceDetails,
    };
    this.spinnerService.show();
    this.fastCheckinService.startForeignParkingPaymentProcess(data).subscribe({next: response => {
      this._isProcessing = false;
      if (response?.success && response.redirectionUrl) {
        window.location.href = response.redirectionUrl;
      } else {
        this.spinnerService.hide();
        this.errorMessage = 'i18n.domain.fast-checkin.errors.startPaymentProcessFailed';
        this.showError = true;
      }
    }, error: err => {
      this.spinnerService.hide();
      this.errorMessage = 'i18n.domain.fast-checkin.errors.startPaymentProcessFailed';
      this.showError = true;
      this._isProcessing = false;
    }});
  }

  private checkInWithoutPayment(duration: number) {
    if (this._isProcessing) {
      return;
    }
    this._isProcessing = true;

    const data : ICheckinUserForeignParkingFastCheckinNoPaymentDTO = {
      zoneId: this.zoneInfo.id,
      email: this.userInfoForm.controls.email.value,
      controlPlateNumber: this.userInfoForm.controls.controlPlate.value,
      controlPlateCountryCode: null,
      controlPlateType: null,
      maxIntendedParkingDurationMinutes: duration,
      selectedSubscriptionId: this.selectedSubscriptionId,
      parkingSpaceNumber: undefined, // TODO
      parkingPriceDetails: this.fastCheckinService.lastParkingPriceResult?.parkingPriceDetails,
      fallbackParkingPriceDetails: this.fastCheckinService.lastParkingPriceResult?.fallbackParkingPriceDetails,
    };
    this.spinnerService.show();
    this.fastCheckinService.checkInWithoutPayment(data).subscribe({next: response => {
      this.spinnerService.hide();
      this._isProcessing = false;
      if (response?.altSessionId) {
        this.router.navigate([response.altSessionId], {relativeTo: this.route});
      } else {
        this.errorMessage = 'i18n.domain.fast-checkin.errors.session-start-failed';
        this.showError = true;
      }
    }, error: err => {
      this.spinnerService.hide();
      this.errorMessage = 'i18n.domain.fast-checkin.errors.session-start-failed';
      this.showError = true;
      this._isProcessing = false;
    }});
  }
}
